import type { Theme } from "@mui/material";
import { lighten } from "@mui/material/styles";
import { GRID_TINT } from "../../../../theme/tokens";

/**
 * Per-week `:has()` hover-tint rules for the grid's week headers: hovering any
 * cell in week `w` tints that week's header. One selector rule per week.
 *
 * Returned as a narrow `Record<string, { backgroundColor: string }>` on purpose.
 * Building these rules inline and spreading the `Object.fromEntries` result
 * straight into MUI's `sx` prop makes TS infer a union past its representable
 * ceiling — TS2590 "union type too complex to represent" — which surfaces under
 * vite-plugin-checker's incremental re-check on HMR (e.g. after a branch switch).
 * Isolating them behind this declared return type turns that into one cheap
 * assignability check. Consume via the array form of `sx`/`containerSx`:
 *
 *   containerSx={[
 *     { "& .ms3-week-header": { transition: "background-color 150ms" } },
 *     (theme) => weekHoverTintRules(theme, weeks),
 *   ]}
 */
export type WeekHoverTint = Record<string, { backgroundColor: string }>;

export const weekHoverTintRules = (theme: Theme, weeks: number): WeekHoverTint =>
	Object.fromEntries(
		Array.from({ length: weeks }, (_, w): [string, { backgroundColor: string }] => [
			`&:has(.ms3-cell[data-week='${w}']:hover) .ms3-week-header[data-week='${w}']`,
			{ backgroundColor: lighten(theme.palette.primary.light, GRID_TINT.HOVER_WASH) },
		]),
	);
